import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ArrowLeft, Plus, Trash2, Megaphone, Users } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { requireAccountType, clearHostSession } from "@/lib/hostAuth";

interface Announcement {
  id: string;
  title: string;
  message: string;
  priority: number;
  is_active: boolean;
  expires_at: string | null;
}

const HostSettingsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [venueId, setVenueId] = useState<string | null>(null);
  const [hostId, setHostId] = useState<string | null>(null);

  // Queue rules
  const [requestLimit, setRequestLimit] = useState(100);
  const [allowRequests, setAllowRequests] = useState(true);
  const [autoRemove, setAutoRemove] = useState(false);
  const [savingRules, setSavingRules] = useState(false);

  // Announcements
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loadingAnn, setLoadingAnn] = useState(true);

  // New announcement form
  const [newTitle, setNewTitle] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [newPriority, setNewPriority] = useState(0);
  const [newExpiresAt, setNewExpiresAt] = useState("");
  const [addingAnn, setAddingAnn] = useState(false);

  // ── Auth guard ──────────────────────────────────────────────────────────────
  useEffect(() => {
    requireAccountType("host").then((result) => {
      if (!result.valid) {
        clearHostSession();
        navigate("/show-host");
        return;
      }
      const hid = localStorage.getItem("hostId");
      const vid = localStorage.getItem("currentVenueId");
      setHostId(hid);
      setVenueId(vid);
    });
  }, [navigate]);

  // ── Load venue queue rules ──────────────────────────────────────────────────
  useEffect(() => {
    if (!venueId) return;
    (supabase.from("venues") as any)
      .select("request_limit, allow_requests, auto_remove")
      .eq("id", venueId)
      .single()
      .then(({ data }: any) => {
        if (data) {
          setRequestLimit(typeof data.request_limit === "number" ? data.request_limit : 100);
          setAllowRequests(data.allow_requests !== false);
          setAutoRemove(!!data.auto_remove);
        }
      });
  }, [venueId]);

  // ── Load announcements ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!venueId) return;
    setLoadingAnn(true);
    (supabase as any).from("announcements")
      .select("id, title, message, priority, is_active, expires_at")
      .eq("venue_id", venueId)
      .order("priority", { ascending: false })
      .then(({ data }: any) => {
        setAnnouncements(data || []);
        setLoadingAnn(false);
      });
  }, [venueId]);

  // ── Save queue rules ────────────────────────────────────────────────────────
  const saveQueueRules = async () => {
    if (!hostId || !venueId) return;
    setSavingRules(true);
    try {
      const { error } = await (supabase.rpc as any)("update_venue_queue_rules", {
        p_session_token: localStorage.getItem("hostSessionToken"),
        p_venue_id: venueId,
        p_request_limit: requestLimit,
        p_allow_requests: allowRequests,
        p_auto_remove: autoRemove,
      });
      if (error) throw error;
      toast({ title: "Queue rules saved" });
    } catch (e) {
      console.error(e);
      toast({ title: "Error", description: "Failed to save queue rules", variant: "destructive" });
    } finally {
      setSavingRules(false);
    }
  };

  // ── Add announcement ────────────────────────────────────────────────────────
  const addAnnouncement = async () => {
    if (!hostId || !venueId || !newTitle.trim() || !newMessage.trim()) return;
    setAddingAnn(true);
    try {
      const newId = crypto.randomUUID();
      const { data, error } = await (supabase.rpc as any)("upsert_announcement", {
        p_session_token: localStorage.getItem("hostSessionToken"),
        p_venue_id: venueId,
        p_announcement_id: newId,
        p_title: newTitle.trim(),
        p_message: newMessage.trim(),
        p_priority: newPriority,
        p_is_active: true,
        p_expires_at: newExpiresAt || null,
      });
      if (error) throw error;
      setAnnouncements((prev) => [
        { id: data ?? newId, title: newTitle.trim(), message: newMessage.trim(), priority: newPriority, is_active: true, expires_at: newExpiresAt || null },
        ...prev,
      ]);
      setNewTitle(""); setNewMessage(""); setNewPriority(0); setNewExpiresAt("");
      toast({ title: "Announcement added" });
    } catch (e) {
      console.error(e);
      toast({ title: "Error", description: "Failed to add announcement", variant: "destructive" });
    } finally {
      setAddingAnn(false);
    }
  };

  // ── Toggle announcement active ──────────────────────────────────────────────
  const toggleAnnouncement = async (ann: Announcement) => {
    if (!hostId || !venueId) return;
    try {
      const { error } = await (supabase.rpc as any)("upsert_announcement", {
        p_session_token: localStorage.getItem("hostSessionToken"),
        p_venue_id: venueId,
        p_announcement_id: ann.id,
        p_title: ann.title,
        p_message: ann.message,
        p_priority: ann.priority,
        p_is_active: !ann.is_active,
        p_expires_at: ann.expires_at,
      });
      if (error) throw error;
      setAnnouncements((prev) =>
        prev.map((a) => a.id === ann.id ? { ...a, is_active: !a.is_active } : a)
      );
    } catch (e) {
      toast({ title: "Error", description: "Failed to update announcement", variant: "destructive" });
    }
  };

  // ── Delete announcement ─────────────────────────────────────────────────────
  const deleteAnnouncement = async (annId: string) => {
    if (!hostId || !confirm("Delete this announcement?")) return;
    try {
      const { error } = await (supabase.rpc as any)("delete_announcement", {
        p_session_token: localStorage.getItem("hostSessionToken"),
        p_announcement_id: annId,
      });
      if (error) throw error;
      setAnnouncements((prev) => prev.filter((a) => a.id !== annId));
      toast({ title: "Announcement deleted" });
    } catch (e) {
      toast({ title: "Error", description: "Failed to delete", variant: "destructive" });
    }
  };

  return (
    <div className="min-h-screen bg-console-bg text-console-text font-geist relative">
      {/* Faint 48px grid */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          backgroundImage:
            'linear-gradient(to right, rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.025) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse at 50% 30%, black 30%, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(ellipse at 50% 30%, black 30%, transparent 80%)',
        }}
      />
      <div className="relative z-10">
      {/* Header */}
      <div className="sticky top-0 z-40 backdrop-blur-sm bg-console-bg/85 border-b border-console-line">
        <div className="container mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center gap-3">
          <Button size="icon" variant="ghost" className="h-11 w-11 sm:h-9 sm:w-9 text-console-muted hover:text-console-text hover:bg-white/5 rounded-[6px]" onClick={() => navigate("/host-show-mode")} title="Back to Show Mode">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <span className="font-geist-mono text-[10px] tracking-[0.18em] uppercase text-console-signal">01 /</span>
          <p className="font-geist text-[14px] font-semibold text-console-text tracking-tight">Host Settings</p>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-6 max-w-2xl space-y-6">

        {/* ── Queue Rules ──────────────────────────────────────────────────── */}
        <Card className="bg-console-surface border-console-line rounded-[10px]">
          <CardHeader>
            <div className="flex items-center gap-2.5">
              <span className="font-geist-mono text-[10px] tracking-[0.18em] uppercase text-console-signal">A /</span>
              <Users className="h-4 w-4 text-console-signal" />
              <CardTitle className="font-geist text-[15px] font-semibold tracking-tight text-console-text">Queue Rules</CardTitle>
            </div>
            <CardDescription className="font-geist text-[12px] text-console-muted mt-1">Control how singer requests are handled tonight</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-geist text-[14px] font-medium text-console-text">Accept Requests</p>
                <p className="font-geist text-[12px] text-console-muted mt-0.5">Allow singers to submit song requests</p>
              </div>
              <Switch checked={allowRequests} onCheckedChange={setAllowRequests} />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-geist text-[14px] font-medium text-console-text">Auto-Remove</p>
                <p className="font-geist text-[12px] text-console-muted mt-0.5">Remove requests automatically after they're sung</p>
              </div>
              <Switch checked={autoRemove} onCheckedChange={setAutoRemove} />
            </div>

            <div className="space-y-1.5">
              <Label className="font-geist-mono text-[10px] tracking-[0.16em] uppercase text-console-muted">Request Limit</Label>
              <Input
                type="number"
                min={1}
                max={500}
                value={requestLimit}
                onChange={(e) => setRequestLimit(Number(e.target.value))}
                className="h-11 bg-console-bg border-console-line-strong text-console-text font-geist w-32 rounded-[6px] focus:border-console-signal focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              <p className="font-geist text-[11px] text-console-dim">Max requests per session (1–500)</p>
            </div>

            <Button
              onClick={saveQueueRules}
              disabled={savingRules}
              className="w-full h-12 font-geist font-semibold text-[14px] bg-console-signal text-console-bg hover:bg-console-signal/90 rounded-[6px]"
            >
              {savingRules ? "Saving…" : "Save Queue Rules"}
            </Button>
          </CardContent>
        </Card>

        {/* ── Announcements ────────────────────────────────────────────────── */}
        <Card className="bg-console-surface border-console-line rounded-[10px]">
          <CardHeader>
            <div className="flex items-center gap-2.5">
              <span className="font-geist-mono text-[10px] tracking-[0.18em] uppercase text-console-signal">B /</span>
              <Megaphone className="h-4 w-4 text-console-signal" />
              <CardTitle className="font-geist text-[15px] font-semibold tracking-tight text-console-text">Announcements</CardTitle>
            </div>
            <CardDescription className="font-geist text-[12px] text-console-muted mt-1">Messages displayed on the singer-facing queue screen</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Add form */}
            <div className="space-y-3 p-4 rounded-[6px] bg-console-bg border border-console-line">
              <p className="font-geist-mono text-[10px] tracking-[0.16em] uppercase text-console-muted">New Announcement</p>
              <div className="space-y-1.5">
                <Label className="font-geist-mono text-[10px] tracking-[0.16em] uppercase text-console-muted">Title</Label>
                <Input
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="e.g. Last call for requests!"
                  className="h-11 bg-console-bg border-console-line-strong text-console-text placeholder:text-console-dim font-geist rounded-[6px] focus:border-console-signal focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="font-geist-mono text-[10px] tracking-[0.16em] uppercase text-console-muted">Message</Label>
                <Textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Announcement text shown to singers…"
                  className="bg-console-bg border-console-line-strong text-console-text placeholder:text-console-dim font-geist rounded-[6px] resize-none focus:border-console-signal focus-visible:ring-0 focus-visible:ring-offset-0"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="font-geist-mono text-[10px] tracking-[0.16em] uppercase text-console-muted">Priority</Label>
                  <Input
                    type="number"
                    min={0}
                    value={newPriority}
                    onChange={(e) => setNewPriority(Number(e.target.value))}
                    className="h-11 bg-console-bg border-console-line-strong text-console-text font-geist rounded-[6px] focus:border-console-signal focus-visible:ring-0 focus-visible:ring-offset-0"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="font-geist-mono text-[10px] tracking-[0.16em] uppercase text-console-muted">Expires at (optional)</Label>
                  <Input
                    type="datetime-local"
                    value={newExpiresAt}
                    onChange={(e) => setNewExpiresAt(e.target.value)}
                    className="h-11 bg-console-bg border-console-line-strong text-console-text font-geist rounded-[6px] focus:border-console-signal focus-visible:ring-0 focus-visible:ring-offset-0"
                  />
                </div>
              </div>
              <Button
                onClick={addAnnouncement}
                disabled={addingAnn || !newTitle.trim() || !newMessage.trim()}
                className="w-full h-12 font-geist font-semibold text-[14px] bg-console-signal text-console-bg hover:bg-console-signal/90 rounded-[6px]"
              >
                <Plus className="h-4 w-4 mr-1" />
                {addingAnn ? "Adding…" : "Add Announcement"}
              </Button>
            </div>

            {/* List */}
            {loadingAnn ? (
              <p className="font-geist text-[13px] text-console-muted text-center py-4">Loading…</p>
            ) : announcements.length === 0 ? (
              <p className="font-geist text-[13px] text-console-dim text-center py-4">No announcements yet</p>
            ) : (
              <div className="space-y-2">
                {announcements.map((ann) => (
                  <div key={ann.id} className={`flex items-start gap-3 p-3 rounded-[6px] border ${ann.is_active ? "bg-console-signal/10 border-console-signal/30" : "bg-console-bg border-console-line opacity-60"}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-geist text-[14px] font-medium text-console-text truncate">{ann.title}</p>
                      <p className="font-geist text-[12px] text-console-muted mt-0.5 line-clamp-2">{ann.message}</p>
                      {ann.expires_at && (
                        <p className="font-geist-mono text-[10px] text-console-dim mt-1">Expires {new Date(ann.expires_at).toLocaleString()}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Switch
                        checked={ann.is_active}
                        onCheckedChange={() => toggleAnnouncement(ann)}
                      />
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-11 w-11 sm:h-9 sm:w-9 text-console-red hover:bg-red-500/10 hover:text-console-red rounded-[6px]"
                        onClick={() => deleteAnnouncement(ann.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      </div>
    </div>
  );
};

export default HostSettingsPage;
